import React, { useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { app } from "../firebase";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  getAuth,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
  getDocs,
  where,
  query,
  collection,
} from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import { useToast } from "../components/ToastProvider";
import {
  LuUser,
  LuMail,
  LuPhone,
  LuLock,
  LuLoaderCircle ,
  LuCheck,
  LuArrowRight
} from "react-icons/lu";

/* ====== CONSTANTES ====== */
const TERMOS_VERSAO = "2025-08-15";

/* ====== ANIMAÇÕES ====== */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

/* ====== ESTILOS (Padrão Visual Unificado) ====== */

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background-color: #3b82f6;
  /* O Fundo Pontilhado Padrão */
  background-image: radial-gradient(#76a9fc 1px, transparent 1px);
  background-size: 20px 20px;
  padding: 1rem;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  width: 100%;
  max-width: 500px;
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  padding: 2rem;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease-out;

  @media (min-width: 640px) {
    padding: 2.5rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 0.95rem;
`;

/* Inputs com Ícones */
const InputGroup = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 0.875rem;
  padding-left: 2.75rem; /* Espaço para o ícone */
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #111827;
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s;

  &:focus {
    background: white;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 1rem 0;
  cursor: pointer;
  user-select: none;

  input {
    appearance: none;
    width: 20px;
    height: 20px;
    border: 2px solid #d1d5db;
    border-radius: 6px;
    margin-top: 2px;
    position: relative;
    transition: all 0.2s;
    cursor: pointer;

    &:checked {
      background-color: #2563eb;
      border-color: #2563eb;
    }

    &:checked::after {
      content: '';
      position: absolute;
      left: 6px;
      top: 2px;
      width: 5px;
      height: 10px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  span {
    font-size: 0.85rem;
    color: #4b5563;
    line-height: 1.4;
    
    a {
      color: #2563eb;
      text-decoration: none;
      font-weight: 500;
      &:hover { text-decoration: underline; }
    }
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 0.875rem;
  border-radius: 12px;
  border: none;
  background-color: ${props => props.disabled ? '#93c5fd' : '#2563eb'};
  color: white;
  font-weight: 600;
  font-size: 1rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s;
  box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2);

  &:hover {
    background-color: ${props => props.disabled ? '#93c5fd' : '#1d4ed8'};
    transform: ${props => props.disabled ? 'none' : 'scale(0.99)'};
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  color: #9ca3af;
  font-size: 0.85rem;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e5e7eb;
  }
  span { padding: 0 1rem; }
`;

const GoogleButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background-color: white;
  color: #374151;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.2s;

  &:hover {
    background-color: #f9fafb;
    border-color: #d1d5db;
  }

  img { width: 20px; height: 20px; }
`;

const FooterLink = styled.div`
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.9rem;
  color: #6b7280;

  a {
    color: #2563eb;
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

const SpinnerIcon = styled(LuLoaderCircle)`
  animation: ${spin} 1s linear infinite;
`;


/* ====== HELPERS ====== */
function digitsOnly(s = "") {
  return s.replace(/\D/g, "");
}

function formatPhoneBR(value = "") {
  const v = digitsOnly(value).slice(0, 11);
  if (v.length <= 2) return `(${v}`;
  if (v.length <= 6) return `(${v.slice(0, 2)}) ${v.slice(2)}`;
  if (v.length <= 10) return `(${v.slice(0, 2)}) ${v.slice(2, 6)}-${v.slice(6)}`;
  return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
}

export default function Cadastro() {
  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefone: "",
    senha: "",
    confirmarSenha: "",
    aceitaTermos: false,
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const { success } = useToast();
  const auth = getAuth(app);
  const db = getFirestore(app);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "telefone") {
      setForm((prev) => ({ ...prev, telefone: formatPhoneBR(value) }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.aceitaTermos) {
      alert("Você precisa aceitar os Termos de Uso para continuar.");
      return;
    }
    if (form.senha !== form.confirmarSenha) {
      alert("As senhas não coincidem.");
      return;
    }

    const phoneDigits = digitsOnly(form.telefone);
    if (phoneDigits.length < 10 || phoneDigits.length > 11) {
      alert("Informe um telefone válido.");
      return;
    }

    setIsLoading(true);

    try {
      // 1. Cria usuário Auth
      const { user } = await createUserWithEmailAndPassword(
        auth,
        form.email,
        form.senha
      );

      // 2. Salva no Firestore
      await setDoc(doc(db, "leads", user.uid), {
        uid: user.uid,
        nome: form.nome.trim(),
        email: form.email.trim().toLowerCase(),
        telefone: form.telefone,
        telefoneRaw: phoneDigits,
        criadoEm: serverTimestamp(),
        statusDaBarbearia: "",
        aceitaTermos: true,
        termosVersao: TERMOS_VERSAO,
        aceitouTermosEm: serverTimestamp(),
      });

      // 3. Sucesso -> Redireciona para Cadastro da Barbearia
      success("Conta criada! Vamos configurar sua barbearia.");
      navigate("/cadastro-barbearia");

    } catch (error) {
      console.error("Erro ao cadastrar:", error);
      alert(
        error?.code === "auth/email-already-in-use"
          ? "Este e-mail já está em uso."
          : error?.message || "Erro no cadastro."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (!form.aceitaTermos) {
      alert("Aceite os termos para continuar com Google.");
      return;
    }

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const uid = user.uid;

      // 1. Verifica/Cria Lead
      const leadRef = doc(db, "leads", uid);
      const leadSnap = await getDoc(leadRef);

      if (!leadSnap.exists()) {
        await setDoc(leadRef, {
            uid: uid,
            nome: user.displayName || "",
            email: user.email,
            telefone: user.phoneNumber || "",
            criadoEm: serverTimestamp(),
            aceitaTermos: true,
            termosVersao: TERMOS_VERSAO,
            aceitouTermosEm: serverTimestamp(),
        });
        // Novo -> Vai para cadastro
        navigate("/cadastro-barbearia");
        return;
      } else {
         // Atualiza termos se necessário
         await setDoc(leadRef, {
            aceitaTermos: true,
            termosVersao: TERMOS_VERSAO,
            aceitouTermosEm: serverTimestamp()
         }, { merge: true });
      }

      // 2. Verifica se é cliente (opcional, dependendo da sua regra de negócio)
      const clienteRef = doc(db, "cliente", uid);
      const clienteSnap = await getDoc(clienteRef);
      if (clienteSnap.exists()) {
        alert("Conta de cliente. Use o app de agendamento.");
        await auth.signOut();
        return;
      }

      // 3. Roteamento Inteligente
      const barbeariaQuery = query(
        collection(db, "barbearia"),
        where("criadorUid", "==", uid)
      );
      const barbeariaSnap = await getDocs(barbeariaQuery);

      if (barbeariaSnap.empty) {
        navigate("/cadastro-barbearia");
      } else {
        const barbeariaDoc = barbeariaSnap.docs[0];
        const infos = barbeariaDoc.data().SituacaoCadastral || {};
        
        if (infos.statusServico !== "ok") {
          navigate("/cadastro-servico");
        } else if (infos.statusBarbeiro !== "ok") {
          navigate("/cadastro-barbeiro");
        } else {
          navigate("/");
        }
      }
    } catch (err) {
      console.error("Erro Google:", err);
      alert("Erro ao logar com Google.");
    }
  };

  return (
    <Container>
      <img src="https://dash.bigode.app/assets/logo-negativa.png" style={{width: '200px', marginBottom: '30px'}} />
      <Card>
        <Header>
          <Title>Crie sua conta</Title>
          <Subtitle>Comece a gerenciar sua barbearia hoje.</Subtitle>
        </Header>

        <form onSubmit={handleSubmit}>
          <InputGroup>
            <InputIcon><LuUser /></InputIcon>
            <StyledInput
              name="nome"
              placeholder="Nome completo"
              value={form.nome}
              onChange={handleChange}
              required
            />
          </InputGroup>

          <InputGroup>
            <InputIcon><LuPhone /></InputIcon>
            <StyledInput
              name="telefone"
              type="tel"
              placeholder="(00) 00000-0000"
              value={form.telefone}
              onChange={handleChange}
              required
              maxLength={16}
            />
          </InputGroup>

          <InputGroup>
            <InputIcon><LuMail /></InputIcon>
            <StyledInput
              name="email"
              type="email"
              placeholder="Seu melhor e-mail"
              value={form.email}
              onChange={handleChange}
              required
            />
          </InputGroup>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <InputGroup style={{ flex: 1 }}>
              <InputIcon><LuLock /></InputIcon>
              <StyledInput
                name="senha"
                type="password"
                placeholder="Senha"
                value={form.senha}
                onChange={handleChange}
                required
              />
            </InputGroup>
            <InputGroup style={{ flex: 1 }}>
              <InputIcon><LuCheck /></InputIcon>
              <StyledInput
                name="confirmarSenha"
                type="password"
                placeholder="Confirmar Senha"
                value={form.confirmarSenha}
                onChange={handleChange}
                required
              />
            </InputGroup>
          </div>

          <CheckboxContainer>
            <input
              type="checkbox"
              name="aceitaTermos"
              checked={form.aceitaTermos}
              onChange={handleChange}
            />
            <span>
              Aceito os <a href="https://bigode.app/termos.html" target="_blank" rel="noreferrer">Termos de Uso</a> e a <a href="https://bigode.app/politicas.html" target="_blank" rel="noreferrer">Política de Privacidade</a>.
            </span>
          </CheckboxContainer>

          <Button type="submit" disabled={isLoading}>
            {isLoading ? <><SpinnerIcon /> Criando...</> : <>Criar Conta Grátis <LuArrowRight /></>}
          </Button>
        </form>

        <Divider><span>ou</span></Divider>

        <GoogleButton type="button" onClick={handleGoogleLogin}>
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="G" />
          Continuar com Google
        </GoogleButton>

        <FooterLink>
          Já tem uma conta? <Link to="/login">Fazer Login</Link>
        </FooterLink>
      </Card>
    </Container>
  );
}